import React, { useState, useEffect, useRef } from 'react'
import DoubleRangeSlider from '../priceRange'
import { ChevronDown, Filter } from 'lucide-react';
import { usePropertySearch, buildPropertySearchParams } from '../api/getCheckProperty';
import { useNavigate } from 'react-router-dom';
import DetailFilter from './detailFilter';
import LoadingScreen from '../../../../loading/loadingScreen';
import FilterIcon from "../../../../assets/images/illustrations/Filter.svg";
import FilterPage from "../filter";

const allowedPropertyTypes = [
  "Residential",
  "Residential Lease",
  "Residential Income",
  "Land",
  "Commercial Sale",
  "Commercial Lease",
  "Farm"
];

const Header = ({ filter, onResults }) => {
  const navigate = useNavigate()
  const priceDropdownRef = useRef(null);
  const autocompleteService = useRef(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0)
  const [filterOpen, setFilterOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const placesService = useRef(null);
  const [localFilters, setLocalFilters] = useState({
    searchCity: filter?.searchCity || '',
    selectedOption: filter?.selectedOption || 'Buy',
    listingType: filter?.listingType || filter?.selectedOption || 'Buy',
    property: filter?.property || null,
    min: filter?.min || 0,
    max: filter?.max || 5000001,
    sqftMin: filter?.sqftMin || 0,
    sqftMax: filter?.sqftMax || 15001,
    bedrooms: filter?.bedrooms || null,
    bathrooms: filter?.bathrooms || null,
    city: filter?.city || '',
    state: filter?.state || '',
    country: filter?.country || 'US',
    street: filter?.street || '',
    streetNumber: filter?.streetNumber || '',
    postalCode: filter?.postalCode || ''
  });

  const extractAddressComponents = (address) => {
  const getLong = (type) =>
    address.address_components?.find(c => c.types.includes(type))?.long_name || "";

  const getShort = (type) =>
    address.address_components?.find(c => c.types.includes(type))?.short_name || "";

  return {
    streetNumber: getLong("street_number"),
    street: getLong("route"),
    city: getLong("locality") || getLong("sublocality") || "",
    state: getShort("administrative_area_level_1"),
    country: getShort("country"),
    postalCode: getLong("postal_code"),
  };
};

const parseWithGoogle = (searchText) => {
  return new Promise((resolve) => {
    if (!placesService.current) {
      resolve({ unparsedAddress: searchText });
      return;
    }
    
    const request = {
      query: searchText,
      fields: ['place_id']
    };
    
    placesService.current.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results[0]) {
        placesService.current.getDetails(
          { placeId: results[0].place_id },
          (place, detailStatus) => {
            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
              const parsed = extractAddressComponents(place);
              resolve(parsed);
            } else {
              resolve({ unparsedAddress: searchText });
            }
          }
        );
      } else {
        resolve({ unparsedAddress: searchText });
      }
    });
  });
};

  const { checkProperty, error } = usePropertySearch();

  // Sync with parent filter changes
  useEffect(() => {
    setLocalFilters({
      searchCity: filter?.searchCity || '',
      selectedOption: filter?.selectedOption || 'Buy',
      listingType: filter?.listingType || filter?.selectedOption || 'Buy',
      property: filter?.property || null,
      min: filter?.min || 0,
      max: filter?.max || 5000001,
      sqftMin: filter?.sqftMin || 0,
      sqftMax: filter?.sqftMax || 15001,
      bedrooms: filter?.bedrooms || null,
      bathrooms: filter?.bathrooms || null,
      city: filter?.city || '',
      state: filter?.state || '',
      country: filter?.country || 'US',
      street: filter?.street || '',
      streetNumber: filter?.streetNumber || '',
      postalCode: filter?.postalCode || ''
    });
  }, [filter]);

  useEffect(() => {
    const initializeGoogleServices = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(document.createElement("div"));
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = initializeGoogleServices;
      document.head.appendChild(script);
    } else {
      initializeGoogleServices();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(event.target)) {
        if (dropdownOpen === 'price') {
          setDropdownOpen('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  let interval;
  useEffect(() => {
    interval = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 90) return oldProgress;
        return oldProgress + 5;
      });
    }, 700);

    return () => clearInterval(interval);
  }, []);

  const toggleDropdown = (dropdown) => {
    setDropdownOpen(dropdownOpen === dropdown ? '' : dropdown);
  };

  const updateFilter = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePriceChange = ({ min, max }) => {
    setLocalFilters(prev => ({ ...prev, min, max }));
  };

  const handlePriceApply = async () => {
    const loc =   { city: localFilters.city, state: localFilters.state, country: localFilters.country } 
    const updatedFilters = { ...localFilters, ...loc, listingType: localFilters.selectedOption };
    setDropdownOpen('');
    setLoading(true);
    
    // Filter out default No min/No max values for API
    const apiFilters = { ...updatedFilters };
    if (updatedFilters.min === 0) delete apiFilters.min;
    if (updatedFilters.max === 5000001) delete apiFilters.max;
    if (updatedFilters.sqftMin === 0) delete apiFilters.sqftMin;
    if (updatedFilters.sqftMax === 15001) delete apiFilters.sqftMax;
    
    const data = await checkProperty(apiFilters);
    setLoading(false);
    if (data) {
      navigate(`/details/properties?${buildPropertySearchParams(updatedFilters).toString()}`, { state: { data, filters: updatedFilters } });
    }
  };

  const handleBuyRentChange = (option) => {
    const updatedFilters = { ...localFilters, selectedOption: option, listingType: option };
    setLocalFilters(updatedFilters);
    setDropdownOpen('');
    if (onResults) {
      onResults(updatedFilters);
    }
  };

  const handlePropertyTypeChange = async (propertyType) => {
    const loc = { city: localFilters.city, state: localFilters.state, country: localFilters.country }
    const updatedFilters = { ...localFilters, property: propertyType, ...loc, listingType: localFilters.selectedOption };
    setLocalFilters(updatedFilters);
    setDropdownOpen('');
    setLoading(true);
   const data = await checkProperty(updatedFilters);
    setLoading(false);
    if (data) {
      navigate(`/details/properties?${buildPropertySearchParams(updatedFilters).toString()}`, { state: { data, filters: updatedFilters } });
    }
  };

  const directSearch = async () => {
    if (!localFilters.searchCity) {
      alert("Please enter a location");
      return;
    }

    let finalFilters;
    
    if (!localFilters.city) {
      // Use Google Places to parse the search text
      const parsed = await parseWithGoogle(localFilters.searchCity);
      finalFilters = {
        ...localFilters,
        ...parsed,
        listingType: localFilters.selectedOption
      };
    } else {
      // Use already parsed data
      finalFilters = {
        ...localFilters,
        listingType: localFilters.selectedOption
      };
    }

    setDropdownOpen('');
    setLoading(true);
        const data = await checkProperty(finalFilters);
    setLoading(false);
    
    if (data) {
      navigate(`/details/properties?${buildPropertySearchParams(finalFilters).toString()}`, { state: { data, filters: finalFilters } });
    }
  };

  const filteredSearch = async () => {
    setDropdownOpen('');
    if (onResults) {
      onResults(localFilters);
    }
  };

  const handleFilterSave = async (values) => {
    const loc =  { city: localFilters.city, state: localFilters.state, country: localFilters.country } 
    // const loc = parseLocation(localFilters.searchCity);
    const f = { ...values, searchCity: localFilters.searchCity, selectedOption: localFilters.selectedOption, ...loc, listingType: localFilters.selectedOption }
    setLocalFilters(f)
    setFilterOpen(false);
    
    if (!loc.city) return;
    
    setLoading(true)
    
    // Filter out default No min/No max values for API
    const apiFilters = { ...f };
    if (f.min === 0) delete apiFilters.min;
    if (f.max === 5000001) delete apiFilters.max;
    if (f.sqftMin === 0) delete apiFilters.sqftMin;
    if (f.sqftMax === 15001) delete apiFilters.sqftMax;
    
        const data = await checkProperty(apiFilters);
    setLoading(false)
    if (data) {
      navigate(`/details/properties?${buildPropertySearchParams(f).toString()}`, { state: { data, filters: f } });
    }
  };
  return (
    <div className="border-b border-gray-200">
      {loading && <LoadingScreen progress={progress} />}
      <div className="flex items-center py-4">
        <div className="flex flex-1 items-center gap-2 min-w-0">
          <div className="relative flex-1 min-w-0 xl:max-w-xl">
            <div className="flex">
              <input
                type="text"
                className="
                      flex-1
                      h-14
                      rounded-2xl
                      border
                      border-gray-200
                      bg-white
                      px-5
                      pr-16
                      text-gray-900
                      placeholder:text-gray-400
                      shadow-sm
                      transition-all
                      duration-200
                      focus:outline-none
                      focus:ring-4
                      focus:ring-[#A61E22]/10
                      focus:border-[#A61E22]
                    "
                value={localFilters.searchCity}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFilter('searchCity', value);
                  
                  // Clear location data when search text changes
                  if (value !== localFilters.searchCity) {
                    setLocalFilters(prev => ({
                      ...prev,
                      searchCity: value,
                      city: '',
                      state: '',
                      country: '',
                      street: '',
                      streetNumber: '',
                      postalCode: ''
                    }));
                  }
                  
                  if (value.length > 2 && autocompleteService.current) {
                    autocompleteService.current.getPlacePredictions(
                      {
                        input: value,
                        componentRestrictions: { country: 'us' }
                      },
                      (predictions, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                          setSuggestions(predictions);
                          setShowDropdown(true);
                        } else {
                          setSuggestions([]);
                          setShowDropdown(false);
                        }
                      }
                    );
                  } else {
                    setSuggestions([]);
                    setShowDropdown(false);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowDropdown(false), 100);
                }}
                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                placeholder="Enter city, ZIP code or neighborhood..."
              />
              
              {showDropdown && suggestions.length > 0 && (
                <div className="dropdown-container absolute top-full left-0 right-0 bg-white border border-gray-200 shadow-lg z-50 max-h-60 overflow-y-auto rounded-b-lg">
                  {suggestions.map((suggestion, index) => {
                         const getLocationType = (types) => {
                           if (types?.includes("locality")) return "City";
                           if (types?.includes("administrative_area_level_2")) return "County";
                           if (types?.includes("administrative_area_level_1")) return "State";
                           if (types?.includes("neighborhood")) return "Neighborhood";
                           if (types?.includes("sublocality")) return "Area";
                           if (types?.includes("postal_code")) return "ZIP Code";
                           if (types?.includes("route")) return "Street";
                           return "Location";
                         };

                    return (
                      <div
                        key={suggestion.place_id || index}
                        className="flex items-center  text-left px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"        
                                 onMouseDown={(e) => {
                           e.preventDefault();
                    if(!placesService.current) return;
  placesService.current.getDetails(
    { placeId: suggestion.place_id },
    (place) => {
      if (!place) return;

      const parsed = extractAddressComponents(place);
      setLocalFilters({
        ...localFilters,
        searchCity: suggestion.description,
        city: parsed.city,
        state: parsed.state,
        country: parsed.country,
        street: parsed.street,
        streetNumber: parsed.streetNumber,
        postalCode: parsed.postalCode
      });

      setSuggestions([]);
      setShowDropdown(false);
    }
  );
}}

                      >
                        {/* Map Icon */}
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <svg 
                            className="w-4 h-4 text-gray-600" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        
                        {/* Location Details */}
                        <div className="flex-1 min-w-0 font-dmsans">
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {suggestion.description}
                          </div>
                          <div className="text-[10px] text-gray-500 font-light font-playfair">
                            {getLocationType(suggestion.types || [])}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
            <button
  onClick={directSearch}
  disabled={loading}
  aria-label="Search"
  className="
    absolute
    right-3
    top-2
    bottom-2
    w-10
    md:w-12
    rounded-xl
    bg-[#A61E22]
    hover:bg-[#8d181b]
    text-white
    shadow-lg
    transition-all
    duration-300
    hover:scale-[1.02]
    disabled:opacity-60
    disabled:cursor-not-allowed
    flex
    items-center
    justify-center
  "
>
  {loading ? (
    <svg
      className="w-5 h-5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5 md:w-6 md:h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-5.2-5.2m2.2-5.3a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  )}
</button>
            </div>
          </div>

         <div className="flex items-center gap-3 lg:gap-4">
            {(() => {
  const filterCount =
    Number(!!localFilters.bedrooms) +
    Number(!!localFilters.bathrooms) +
    Number(!!localFilters.property) +
    Number(localFilters.min > 0 || localFilters.max < 5000001) +
    Number(localFilters.sqftMin > 0 || localFilters.sqftMax < 15001);

                  return (
                    <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  aria-label="Open filters"
                  className="
                  relative
                  w-14
                  h-14
                  rounded-2xl
                  border
                  border-gray-200
                  bg-white
                  hover:bg-gray-50
                  hover:shadow-md
                  transition-all
                  duration-200
                  flex
                  items-center
                  justify-center
                "
                >
  <div className="relative">

    <img
      src={FilterIcon}
      alt="Filters"
      className={`
        w-5
        h-5
        transition-all
        duration-200
        ${
          filterCount > 0
            ? "opacity-100 scale-105"
            : "opacity-70"
        }
      `}
    />

    {filterCount > 0 && (
      <span
        className="
          absolute
          -top-2
          -right-2
          min-w-[18px]
          h-[18px]
          px-1
          rounded-full
          bg-[#A61E22]
          text-white
          text-[10px]
          font-bold
          leading-none
          flex
          items-center
          justify-center
          shadow-md
          ring-2
          ring-white
        "
      >
        {filterCount}
      </span>
    )}

  </div>
</button>
  );
})()}
            <div className="relative hidden xl:flex">
 <button
  onClick={() => toggleDropdown("sale")}
  className="
    h-14
    px-5
    rounded-2xl
    border
    border-gray-200
    bg-white
    text-gray-900
    font-medium
    text-sm
    flex
    items-center
    gap-2
    shadow-sm
    hover:bg-gray-50
    hover:shadow-md
    transition-all
    duration-200
  "
>
  <span>
    For {localFilters?.selectedOption === "Buy" ? "Sale" : "Rent"}
  </span>

  <ChevronDown
    size={16}
    className={`transition-transform duration-200 ${
      dropdownOpen === "sale" ? "rotate-180" : ""
    }`}
  />
</button>

{dropdownOpen === "sale" && (
  <div
    className="
      absolute
      top-full
      left-0
      mt-2
      w-52
      bg-white
      rounded-2xl
      border
      border-gray-100
      shadow-xl
      overflow-hidden
      z-20
    "
  >
    <div className="py-2">

      <label
        className="
          flex
          items-center
          gap-3
          px-5
          py-3
          cursor-pointer
          hover:bg-gray-50
          transition-colors
        "
      >
        <input
          type="radio"
          name="buyRent"
          value="Buy"
          checked={localFilters?.selectedOption === "Buy"}
          onChange={() => handleBuyRentChange("Buy")}
          className="
            h-4
            w-4
            accent-[#A61E22]
          "
        />

        <div>
          <div className="text-sm font-medium text-gray-900">
            For Sale
          </div>

          <div className="text-xs text-gray-500">
            Purchase a property
          </div>
        </div>
      </label>

      <label
        className="
          flex
          items-center
          gap-3
          px-5
          py-3
          cursor-pointer
          hover:bg-gray-50
          transition-colors
        "
      >
        <input
          type="radio"
          name="buyRent"
          value="Rent"
          checked={localFilters?.selectedOption === "Rent"}
          onChange={() => handleBuyRentChange("Rent")}
          className="
            h-4
            w-4
            accent-[#A61E22]
          "
        />

        <div>
          <div className="text-sm font-medium text-gray-900">
            For Rent
          </div>

          <div className="text-xs text-gray-500">
            Lease a property
          </div>
        </div>
      </label>

     </div>
    </div>
  )}
</div>


         <div className="relative hidden xl:flex" ref={priceDropdownRef}>

  <button
    onClick={() => toggleDropdown("price")}
    className="
      h-14
      px-5
      rounded-2xl
      border
      border-gray-200
      bg-white
      text-gray-900
      font-medium
      text-sm
      flex
      items-center
      gap-2
      shadow-sm
      hover:bg-gray-50
      hover:shadow-md
      transition-all
      duration-200
    "
  >
    <span>Price</span>

    <ChevronDown
      size={16}
      className={`transition-transform duration-200 ${
        dropdownOpen === "price" ? "rotate-180" : ""
      }`}
    />
  </button>

  {dropdownOpen === "price" && (
    <div
      className="
        absolute
        top-full
        left-0
        mt-2
        w-[330px]
        bg-white
        rounded-2xl
        border
        border-gray-100
        shadow-xl
        z-20
        overflow-hidden
      "
    >
      <div className="p-5">

        <DoubleRangeSlider
          min={localFilters.min}
          max={localFilters.max}
          onChange={handlePriceChange}
          maxRange={5000001}
        />

        <button
          onClick={handlePriceApply}
          className="
            mt-5
            w-full
            h-11
            rounded-xl
            bg-[#A61E22]
            hover:bg-[#8d181b]
            text-white
            font-semibold
            transition-all
            duration-200
          "
        >
          Apply
        </button>

      </div>
    </div>
  )}

</div>
           <div className="relative hidden xl:flex">

  <button
    onClick={() => toggleDropdown("type")}
    className="
      h-14
      px-5
      rounded-2xl
      border
      border-gray-200
      bg-white
      text-gray-900
      font-medium
      text-sm
      flex
      items-center
      gap-2
      shadow-sm
      hover:bg-gray-50
      hover:shadow-md
      transition-all
      duration-200
    "
  >
    <span className="max-w-[130px] truncate">
      {localFilters.property || "Property Type"}
    </span>

    <ChevronDown
      size={16}
      className={`transition-transform duration-200 ${
        dropdownOpen === "type" ? "rotate-180" : ""
      }`}
    />
  </button>

  {dropdownOpen === "type" && (
    <div
      className="
        absolute
        top-full
        left-0
        mt-2
        w-64
        bg-white
        rounded-2xl
        border
        border-gray-100
        shadow-xl
        overflow-hidden
        z-20
      "
    >
      <div className="py-2">

        {allowedPropertyTypes.map((item, index) => (
          <label
            key={index}
            className="
              flex
              items-center
              gap-3
              px-5
              py-3
              cursor-pointer
              hover:bg-gray-50
              transition-colors
            "
          >
            <input
              type="radio"
              name="propertyType"
              value={item}
              checked={localFilters.property === item}
              onChange={() => handlePropertyTypeChange(item)}
              className="
                h-4
                w-4
                accent-[#A61E22]
              "
            />

            <span className="text-sm text-gray-900">
              {item}
            </span>

          </label>
        ))}

      </div>
    </div>
  )}

</div>
            
          </div>
        </div>

         </div>

    {filterOpen && (
  <FilterPage
    close={() => setFilterOpen(false)}
    onSave={handleFilterSave}
    filterVal={localFilters}
  />
)}
    </div>
  );
};

export default Header;